import { BasketOptimizerOptimizeRequest, basketOptimizerApi } from 'apiConnector/basket-optimizer';
import { withAppCrashIfException } from 'epic';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { combineEpics, ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, merge, of, TimeoutError, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
  timeout,
  timeoutWith,
  withLatestFrom,
} from 'rxjs/operators';
import type { Action } from 'state/actions';
import {
  type BasketOptimizerOptimizeIdReceived,
  basketOptimizerOptimizeCanceled,
  basketOptimizerOptimizeIdReceived,
  basketOptimizerOptimizeRequestFailed,
  basketOptimizerResultReceived,
} from 'state/advisory/basket-optimizer/actions';
import type { BasketOptimizerResultResponse } from 'state/advisory/basket-optimizer/model';
import {
  getCouponFrom,
  getCouponTo,
  getIsMutuallyExclusive,
  getTargetBasketSize,
  getUnderlyingTags,
} from 'state/advisory/basket-optimizer/selectors';
import type { EpicWithThunk } from 'state/state.common';
import { getUserPreferencesLanguage } from 'state/userPreferences/userPreferences.selectors';
import { logFrontError } from 'utils/errors/logFrontError';
import { fatEventApiConnector } from 'utils/streaming/api';
import { basketOptimizerResult } from 'utils/streaming/messages';

export const basketOptimizerEpic = () =>
  withAppCrashIfException(
    'basketOptimizer',
    combineEpics(
      optimizeRequested(), // OK
      cancelOptimize(), // OK
      listenToBasketOptimizerResultEpic(),
    ),
  );

export function optimizeRequested(): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    return action$.pipe(
      ofType('BASKETOPTIMIZER_OPTIMIZE_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteId, productType, underlyingsCount }, state]) => {
        const underlyingTags = getUnderlyingTags(state, tabId);
        const validUnderlyingTags = underlyingTags ? underlyingTags.filter(tag => !!tag.rawValue) : [];
        const highlightedValidUnderlyingTags = validUnderlyingTags.filter(tag => !!tag.isHighlighted);

        const params: BasketOptimizerOptimizeRequest = {
          quoteId,
          listUnderlyingsCodes: validUnderlyingTags.map(tag => tag.value),
          pinnedUnderlyingsCodes: highlightedValidUnderlyingTags.map(tag => tag.value),
          isAstroQuote: true,
          productCode: productType,
          language: getUserPreferencesLanguage(state),
          underlyingsCount,
          mutuallyExclusive: getIsMutuallyExclusive(state, tabId)!,
          targetBasketSize: getTargetBasketSize(state, tabId),
          couponFrom: getCouponFrom(state, tabId),
          couponTo: getCouponTo(state, tabId),
        };

        const cancelled$ = action$.pipe(
          ofType('BASKETOPTIMIZER_OPTIMIZE_CANCELED'),
          filter(action => action.tabId === tabId),
        );

        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        return basketOptimizerApi(httpClient)
          .optimize(params)
          .pipe(
            map(({ requestId, estimatedComputationTimeInSeconds }) =>
              basketOptimizerOptimizeIdReceived(tabId, quoteId, requestId, estimatedComputationTimeInSeconds),
            ),
            takeUntil(merge(cancelled$, quoteOrTabRemoved$)),
            catchError(() =>
              of(
                basketOptimizerOptimizeRequestFailed(
                  tabId,
                  'An error occurred when trying to optimize. Please contact the SG Markets SP support.',
                ),
              ),
            ),
          );
      }),
    );
  };
}

export function cancelOptimize(timeout: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (actions$, _state$, { httpClient }) => {
    return actions$.pipe(
      ofType('BASKETOPTIMIZER_OPTIMIZE_CANCELED'),
      mergeMap(({ optimizeId }) => {
        return basketOptimizerApi(httpClient)
          .cancelOptimize(optimizeId)
          .pipe(
            mergeMap(() => EMPTY),
            timeoutWith(timeout, throwError(new Error('Timed out')), appScheduler),
            catchError(_ => {
              return EMPTY;
            }),
          );
      }),
    );
  };
}
function mapResultToAction(response: BasketOptimizerResultResponse, tabId: string): Action {
  if (response.status === 0) {
    return basketOptimizerResultReceived(tabId, response.result);
  } else {
    return basketOptimizerOptimizeRequestFailed(tabId, response.messageError);
  }
}

export function listenToBasketOptimizerResultEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, streaming }) =>
    action$.pipe(
      ofType('BASKETOPTIMIZER_OPTIMIZE_ID_RECEIVED'),
      mergeMap(
        ({ optimizeId, tabId, quoteId, estimatedComputationTimeInSeconds }: BasketOptimizerOptimizeIdReceived) => {
          const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

          const requestCancelled$ = action$.pipe(
            ofType('BASKETOPTIMIZER_OPTIMIZE_CANCELED'),
            filter(action => action.optimizeId === optimizeId),
          );

          return basketOptimizerResult(streaming).pipe(
            // streaming sends all reconciliationId to something called quoteId
            filter(r => r.quoteId === optimizeId),
            timeout(estimatedComputationTimeInSeconds * 1000, appScheduler),
            mergeMap(lightEvent =>
              fatEventApiConnector(oldHttpClient)
                .getFatEvent<BasketOptimizerResultResponse>(lightEvent, true)
                .pipe(catchError(_ => EMPTY)),
            ),
            map(result => mapResultToAction(result, tabId)),
            takeUntil(merge(quoteOrTabRemoved$, requestCancelled$)),
            take(1),
            catchError(err => {
              logFrontError('An error occurred in basketOptimiserResultEpic {@FrontError}', err);
              if (err instanceof TimeoutError) {
                return of(
                  basketOptimizerOptimizeRequestFailed(tabId, 'generic.timeout.message'),
                  basketOptimizerOptimizeCanceled(tabId, optimizeId),
                );
              }
              return of(
                basketOptimizerOptimizeRequestFailed(tabId, 'generic.error.message'),
                basketOptimizerOptimizeCanceled(tabId, optimizeId),
              );
            }),
          );
        },
      ),
    );
}
